/**
 * Settings → Адреси: the firm's own addresses (#420) — the doors the map does not have, placed
 * once and locked.
 *
 * Four things on one page, in the order an administrator comes to them: what waits for their
 * approval, the two switches about who places addresses and how the desk offers entrances, the
 * places themselves with a map beside them, and the CSV in and out (#435). Choosing a place
 * opens it in the editor above the list, with its history under it; *Add* starts from the
 * building and places its doors one click at a time.
 *
 * Here and not under Reports, because Reporting writes nothing and this page does. The report
 * of what to place next (#434) links here, and arrives with the building filled in and the door
 * where the bookings were (#478) — so an address our maps named locks in one click; one Google
 * named carries Google's words and point, and the editor waits for the firm's own.
 *
 * It arrives with the row's key too, and reads what the row is made of from the report: once
 * the place is saved, *Също присъедини…* — on unless switched off — joins the row's old
 * bookings and its callers' addresses to it, ticked as the report's join dialog would tick them
 * against the door just placed. A save refused because the address is already a place offers
 * to join the row to that one instead. A place's joins are in its history, to undo or finish.
 *
 * The row belongs to the draft it arrived with, and to nothing else: the switch names the row,
 * counts what the save would really take from the door as it now stands, and is shown only on
 * that draft. Cancelling it, or starting or opening another place, lets the row go, so a later
 * place on the same visit never takes a row it was not placed for.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { mapApi, type MapConfiguration } from '../../map/api';
import {
  draftOf,
  duplicateOf,
  notifyPlacesChanged,
  placesApi,
  type PlaceDraft,
  type PlaceHistory,
  type PlaceJoinEntry,
  type PlaceJoinList,
  type PlaceList,
  type PlaceReview,
  type PlaceReviewList as ReviewList,
  type PlaceSettings,
  type PlaceView,
} from '../../places/api';
import { writeEntrance } from '../../places/entrances';
import { forgetPlacesFeatures } from '../../places/features';
import { countOptions, defaultJoinCounts, defaultTicked, joinRequest, tickedIds } from '../../reports/addressesQuery';
import { reportsApi, type UnplacedMembers } from '../../reports/api';
import { backFrom, rowFrom } from '../../reports/back';
import type { GoogleNamed } from '../../places/google';
import type { MapPin, MapPoint, MapTarget } from '../../places/PlacesMap';
import {
  Banner,
  Button,
  ConfirmDialog,
  EmptyState,
  Icon,
  Input,
  PageHeader,
  Pill,
  Section,
  Segmented,
  SettingRow,
  Switch,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import { useTenantFormats } from '../../shell/formats';
import { mapHomeApi, newId } from '../api';
import { describe } from '../errors';
import { openingView } from '../map/homeView';
import { PlaceEditor } from './PlaceEditor';
import { PlaceHistoryList } from './PlaceHistoryList';
import { PlaceImportDialog } from './PlaceImportDialog';
import { PlaceReviewList } from './PlaceReviewList';
import { nextEntrance, pinOf, who } from './words';

const PlacesMap = lazy(async () => ({ default: (await import('../../places/PlacesMap')).PlacesMap }));

/** Close enough to tell a block's entrances apart. */
const DOOR_ZOOM = 18;

/**
 * What the editor is doing: placing a new door, or changing one — and, when it came from an
 * address Google named, Google's name and point, which it may not be saved as (#478). `fromRow`
 * marks the one draft opened from a report's row, the only one a save joins that row to.
 */
type Editing = { placeId: string | null; draft: PlaceDraft; google?: GoogleNamed | null; fromRow?: boolean };

const EMPTY: PlaceDraft = { building: '', entrance: null, longitude: 0, latitude: 0, note: null, names: [] };

export function PlacesPage() {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Arriving from a report's row, the way back to it as it was left (#478). Held for the
  // visit: the address bar is cleared below, and several addresses are often locked in one go.
  const [back] = useState(() => backFrom(params));

  // And the row being locked, by its key: what it is made of is read from the report, so the
  // place, once saved, can take the row's old bookings and addresses with it.
  const [row, setRow] = useState(() => rowFrom(params));
  const [rowMembers, setRowMembers] = useState<UnplacedMembers | null>(null);
  const [alsoJoin, setAlsoJoin] = useState(true);

  const [configuration, setConfiguration] = useState<MapConfiguration | null>(null);
  const [view, setView] = useState<{ longitude: number; latitude: number; zoom: number } | null>(null);
  const [list, setList] = useState<PlaceList | null>(null);
  const [review, setReview] = useState<ReviewList | null>(null);
  const [settings, setSettings] = useState<PlaceSettings | null>(null);

  const [filter, setFilter] = useState('');

  // Arriving from the report of what to place next (#434): the building filled in, the map on
  // it, and the door where the bookings were (#478). Read once, as the page's first state; the
  // address bar is cleared below.
  const [editing, setEditing] = useState<Editing | null>(() => {
    const building = params.get('building');

    if (building === null) return null;

    const longitude = Number(params.get('lon'));
    const latitude = Number(params.get('lat'));
    const placed = Number.isFinite(longitude) && Number.isFinite(latitude) && longitude !== 0;
    const google = params.get('google');

    return {
      placeId: null,
      draft: {
        ...EMPTY,
        building,
        entrance: params.get('entrance'),
        longitude: placed ? longitude : 0,
        latitude: placed ? latitude : 0,
      },
      google: google !== null && placed ? { line: google, longitude, latitude } : null,
      fromRow: rowFrom(params) !== null,
    };
  });
  const [picking, setPicking] = useState<'door' | 'stop'>('door');
  const [history, setHistory] = useState<PlaceHistory | null>(null);
  const [joins, setJoins] = useState<PlaceJoinList | null>(null);
  const [shown, setShown] = useState<PlaceReview | null>(null);
  const [target, setTarget] = useState<MapTarget | null>(() => {
    const longitude = Number(params.get('lon'));
    const latitude = Number(params.get('lat'));

    return params.get('building') !== null && Number.isFinite(longitude) && Number.isFinite(latitude) && longitude !== 0
      ? { longitude, latitude, zoom: DOOR_ZOOM, key: 1 }
      : null;
  });

  /** Sends the map somewhere, a second time to the same place included. */
  const jumpTo = (point: MapPoint) =>
    setTarget((was) => ({ ...point, zoom: DOOR_ZOOM, key: (was?.key ?? 0) + 1 }));
  const [importing, setImporting] = useState(false);
  /** Asked before a place goes: it leaves every search, and its history goes with it. */
  const [removing, setRemoving] = useState(false);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** The place a refused save reads the same as, offered instead. */
  const [clash, setClash] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [places, waiting] = await Promise.all([placesApi.list(), placesApi.review()]);

    setList(places);
    setReview(waiting);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [style, home, stored] = await Promise.all([
          mapApi.configuration(),
          mapHomeApi.get(),
          placesApi.settings(),
        ]);

        if (cancelled) return;

        setConfiguration(style);
        setView(openingView(home, style));
        setSettings(stored);
        await reload();
      } catch (failure) {
        if (!cancelled) setProblem(describe(failure, t, 'settings.couldNotLoad'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reload, t]);

  // What the row is made of, once: a row nobody can read any more is simply not joined.
  useEffect(() => {
    if (row === null) return;

    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);

    void reportsApi
      .unplacedMembers({ preset: 'custom', from: row.from ?? today, to: row.to ?? today }, row.key, row.source)
      .then(
        (members) => {
          if (!cancelled) setRowMembers(members);
        },
        () => {
          if (!cancelled) setRow(null);
        },
      );

    return () => {
      cancelled = true;
    };
  }, [row]);

  // The report's address is in the editor now; it should not open again on a reload.
  useEffect(() => {
    if (params.get('building') !== null) setParams({}, { replace: true });
  }, [params, setParams]);

  // The confirmation is a moment, not a state.
  useEffect(() => {
    if (notice === null) return;

    const timer = globalThis.setTimeout(() => setNotice(null), 4000);

    return () => globalThis.clearTimeout(timer);
  }, [notice]);

  const people = useMemo(
    () => ({ ...(list?.people ?? {}), ...(review?.people ?? {}), ...(history?.people ?? {}), ...(joins?.people ?? {}) }),
    [list, review, history, joins],
  );

  const shownPlaces = useMemo(() => {
    const words = filter.trim().toLocaleLowerCase('bg');

    return (list?.places ?? []).filter(
      (place) =>
        words.length === 0 ||
        place.line.toLocaleLowerCase('bg').includes(words) ||
        place.names.some((name) => name.toLocaleLowerCase('bg').includes(words)),
    );
  }, [list, filter]);

  // On the map: every door, and — while a review row is shown — where it stood before.
  const pins: MapPin[] = useMemo(() => {
    const all = (list?.places ?? []).map(pinOf);

    if (shown?.before != null) {
      all.push({
        id: `before-${shown.place.id}`,
        longitude: shown.before.door.longitude,
        latitude: shown.before.door.latitude,
        label: '←',
        title: t('places.review.before'),
        tone: 'waiting',
      });
    }

    return all;
  }, [list, shown, t]);

  async function act(work: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setProblem(null);
    setClash(null);

    try {
      await work();
      await reload();
      notifyPlacesChanged();
      if (done !== undefined) setNotice(done);
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  /** A place's history and its joins, read together. */
  function readHistory(id: string) {
    setHistory(null);
    setJoins(null);
    void placesApi.history(id).then(setHistory, () => undefined);
    void placesApi.joins(id).then(setJoins, () => undefined);
  }

  /** The report's row let go: another draft has taken the place of the one it arrived with. */
  function leaveRow() {
    setRow(null);
    setRowMembers(null);
  }

  function open(place: PlaceView) {
    leaveRow();
    setProblem(null);
    setClash(null);
    setEditing({ placeId: place.id, draft: draftOf(place) });
    setPicking('door');
    setShown(null);
    jumpTo(place.door);
    readHistory(place.id);
  }

  // The door as it now stands, or the row's own point before one is put down.
  const placedLongitude = editing?.draft.longitude ?? 0;
  const placedLatitude = editing?.draft.latitude ?? 0;

  /**
   * How many of the row's bookings and addresses the save would join: the same ticks `joinRow`
   * makes, from the door as it now stands, so the switch follows the door as it is moved.
   */
  const rowCounts = useMemo(() => {
    if (rowMembers === null) return { orders: 0, addresses: 0 };

    const from =
      placedLongitude !== 0
        ? { longitude: placedLongitude, latitude: placedLatitude }
        : { longitude: rowMembers.longitude ?? null, latitude: rowMembers.latitude ?? null };

    return defaultJoinCounts(rowMembers.members, from);
  }, [rowMembers, placedLongitude, placedLatitude]);

  const rowLabel =
    rowMembers === null
      ? ''
      : t('places.alsoJoin', {
          line: rowMembers.line,
          orders: t('counts.bookings', countOptions(rowCounts.orders, formats.count)),
          addresses: t('counts.addresses', countOptions(rowCounts.addresses, formats.count)),
        });

  /**
   * Joins the row being locked to a place: its members ticked against that place's door, as the
   * report's dialog would tick them. Once only — the row is let go afterwards, whatever happened.
   */
  async function joinRow(place: PlaceView): Promise<string | null> {
    if (row === null || rowMembers === null) return null;

    const picked = tickedIds(defaultTicked(rowMembers.members, place.door));

    setRow(null);
    setRowMembers(null);

    if (picked.size === 0) return null;

    const result = await placesApi.join(
      place.id,
      joinRequest(newId(), { key: row.key, line: rowMembers.line, source: row.source, from: row.from, to: row.to }, rowMembers.members, picked),
    );

    return t('places.joinedAfterSave', {
      line: place.line,
      orders: t('counts.bookings', countOptions(result.orders + result.destinations, formats.count)),
      addresses: t('counts.addresses', countOptions(result.addresses, formats.count)),
    });
  }

  function pick(point: MapPoint) {
    if (editing === null) return;

    setEditing({
      ...editing,
      draft:
        picking === 'door'
          ? { ...editing.draft, longitude: point.longitude, latitude: point.latitude }
          : { ...editing.draft, stopLongitude: point.longitude, stopLatitude: point.latitude },
    });
  }

  async function save(): Promise<PlaceView | null> {
    if (editing === null) return null;

    setBusy(true);
    setProblem(null);
    setClash(null);

    try {
      const saved =
        editing.placeId === null
          ? await placesApi.add({ ...editing.draft, google: editing.google ?? null })
          : await placesApi.update(editing.placeId, editing.draft);

      // Placed from a report's row: the row's old bookings and addresses go with it, unless asked
      // not to. The place is saved whatever the join does, so a join that fails says so and the
      // editor still moves on to the place — saving again would only be refused as a duplicate.
      let joined: string | null = null;
      let joinFailure: unknown = null;

      if (editing.placeId === null && editing.fromRow === true && alsoJoin) {
        try {
          joined = await joinRow(saved);
        } catch (failure) {
          joinFailure = failure;
        }
      }

      await reload();
      notifyPlacesChanged();

      if (joinFailure !== null) setProblem(describe(joinFailure, t, 'settings.couldNotSave'));
      else setNotice(joined ?? t('places.saved', { line: saved.line }));

      return saved;
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotSave'));
      setClash(duplicateOf(failure));

      return null;
    } finally {
      setBusy(false);
    }
  }

  async function saveSettings(next: PlaceSettings) {
    setSettings(next);

    try {
      setSettings(await placesApi.saveSettings(next));
      forgetPlacesFeatures();
      setNotice(t('settings.saved'));
    } catch (failure) {
      setProblem(describe(failure, t, 'settings.couldNotSave'));
    }
  }

  const clashing = clash === null ? undefined : list?.places.find((place) => place.id === clash);

  /** Joins the row to the place the save clashed with, and opens it. */
  function joinExisting(place: PlaceView) {
    void act(async () => {
      const done = await joinRow(place);

      open(place);
      if (done !== null) setNotice(done);
    });
  }

  /** Undoes or finishes one of the open place's joins, and reads its history again. */
  function answerJoin(join: PlaceJoinEntry, how: 'undo' | 'finish') {
    const id = editing?.placeId;

    if (id == null) return;

    void act(
      () => (how === 'undo' ? placesApi.undoJoin(id, join.joinId) : placesApi.finishJoin(id, join.joinId)),
      t(how === 'undo' ? 'places.joins.undoneNotice' : 'places.joins.finished'),
    ).then(() => readHistory(id));
  }

  const editingDoor =
    editing === null || editing.draft.longitude === 0
      ? null
      : { longitude: editing.draft.longitude, latitude: editing.draft.latitude };
  const editingStop =
    editing?.draft.stopLongitude != null && editing.draft.stopLatitude != null
      ? { longitude: editing.draft.stopLongitude, latitude: editing.draft.stopLatitude }
      : null;

  return (
    <>
      <PageHeader
        title={t('nav.settings.places')}
        description={t('nav.settingsAbout.places')}
        actions={
          <>
            {back !== null && (
              <Button variant="primary" onClick={() => navigate(back)}>
                <Icon name="back" small />
                {t('places.backToReport')}
              </Button>
            )}
            <Button onClick={() => void placesApi.export().catch((failure: unknown) => setProblem(describe(failure, t, 'settings.couldNotLoad')))}>
              <Icon name="download" small />
              {t('places.export')}
            </Button>
            <Button onClick={() => setImporting(true)}>{t('places.import.open')}</Button>
          </>
        }
      />

      {problem !== null && (
        <Banner
          tone="danger"
          actions={
            clashing !== undefined && (
              <>
                {editing?.fromRow === true && row !== null && rowMembers !== null && (
                  <Button disabled={busy} onClick={() => joinExisting(clashing)}>
                    {t('places.joinExisting')}
                  </Button>
                )}
                <Button onClick={() => open(clashing)}>{t('places.openExisting')}</Button>
              </>
            )
          }
        >
          {problem}
        </Banner>
      )}
      {notice !== null && <Banner tone="ok">{notice}</Banner>}

      {list === null || configuration === null || view === null || settings === null ? (
        problem === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,560px)_minmax(0,1fr)] gap-4 items-start">
          <div className="flex flex-col gap-4 min-w-0">
            <PlaceReviewList
              items={review?.items ?? []}
              people={people}
              busy={busy}
              onShow={(item) => {
                leaveRow();
                setShown(item);
                setEditing(null);
                jumpTo(item.suggested?.door ?? item.place.door);
              }}
              onApprove={(item) =>
                void act(
                  () => (item.kind === 'reported' ? placesApi.approveReport(item.changeId!) : placesApi.approve(item.place.id)),
                  t('places.review.approved'),
                ).then(() => setShown(null))
              }
              onReject={(item) =>
                void act(
                  () => (item.kind === 'reported' ? placesApi.rejectReport(item.changeId!) : placesApi.reject(item.place.id)),
                  t('places.review.rejected'),
                ).then(() => setShown(null))
              }
            />

            <Section title={t('places.settings.title')}>
              <SettingRow label={t('places.settings.dispatchers')} help={t('places.settings.dispatchersHelp')}>
                <Switch
                  checked={settings.dispatchersPlace}
                  label={t('places.settings.dispatchers')}
                  onChange={(next) => void saveSettings({ ...settings, dispatchersPlace: next })}
                />
              </SettingRow>
              <SettingRow label={t('places.settings.entrances')} help={t('places.settings.entrancesHelp')} last>
                <Segmented
                  label={t('places.settings.entrances')}
                  value={settings.entrancesAfterAddress ? 'after' : 'list'}
                  options={[
                    { value: 'list', label: t('person.entrancesWay.list') },
                    { value: 'after', label: t('person.entrancesWay.after') },
                  ]}
                  onChange={(next) => void saveSettings({ ...settings, entrancesAfterAddress: next === 'after' })}
                />
              </SettingRow>
            </Section>

            {editing !== null && (
              <PlaceEditor
                key={editing.placeId ?? 'new'}
                draft={editing.draft}
                google={editing.google ?? null}
                isNew={editing.placeId === null}
                picking={picking}
                busy={busy}
                problem={null}
                onChange={(draft) => setEditing({ ...editing, draft })}
                onPicking={setPicking}
                onJump={jumpTo}
                onCancel={() => {
                  leaveRow();
                  setEditing(null);
                  setHistory(null);
                  setJoins(null);
                }}
                onSave={() =>
                  void save().then((saved) => {
                    if (saved === null) return;

                    if (editing.placeId === null) {
                      // Placed: the next door of the same block is one click away.
                      setEditing({ placeId: saved.id, draft: draftOf(saved) });
                      readHistory(saved.id);
                    } else {
                      setEditing(null);
                      setHistory(null);
                      setJoins(null);
                    }
                  })
                }
                onDelete={editing.placeId === null ? undefined : () => setRemoving(true)}
                onNext={
                  editing.placeId === null || editing.draft.entrance == null
                    ? undefined
                    : () => {
                        leaveRow();
                        setEditing({
                          placeId: null,
                          draft: { ...EMPTY, building: editing.draft.building, entrance: nextEntrance(editing.draft.entrance!) || null },
                        });
                        setHistory(null);
                        setPicking('door');
                      }
                }
              />
            )}

            {editing?.placeId === null &&
              editing.fromRow === true &&
              row !== null &&
              rowMembers !== null &&
              rowCounts.orders + rowCounts.addresses > 0 && (
                <div className="flex items-center justify-between gap-4 px-1">
                  <span className="text-body">{rowLabel}</span>
                  <Switch checked={alsoJoin} disabled={busy} label={rowLabel} onChange={setAlsoJoin} />
                </div>
              )}

            {editing?.placeId != null && history !== null && history.entries.length > 0 && (
              <PlaceHistoryList
                entries={history.entries}
                joins={joins?.joins ?? []}
                people={people}
                busy={busy}
                onUndoJoin={(join) => answerJoin(join, 'undo')}
                onFinishJoin={(join) => answerJoin(join, 'finish')}
                onRestore={(entry) =>
                  void act(async () => {
                    const restored = await placesApi.restore(editing.placeId!, entry.id);

                    setEditing({ placeId: restored.id, draft: draftOf(restored) });
                    setHistory(await placesApi.history(restored.id));
                    setJoins(await placesApi.joins(restored.id));
                  }, t('places.history.restored'))
                }
              />
            )}

            <Section
              title={t('places.list.title', { count: list.places.length })}
              actions={
                <Button
                  variant="primary"
                  onClick={() => {
                    leaveRow();
                    setEditing({ placeId: null, draft: EMPTY });
                    setHistory(null);
                    setShown(null);
                    setPicking('door');
                  }}
                >
                  <Icon name="plus" small />
                  {t('places.add')}
                </Button>
              }
            >
              <Input
                value={filter}
                placeholder={t('places.list.filter')}
                onChange={(event) => setFilter(event.target.value)}
                className="mb-2"
              />

              {shownPlaces.length === 0 ? (
                <EmptyState>{list.places.length === 0 ? t('places.list.empty') : t('places.list.noMatch')}</EmptyState>
              ) : (
                <Table tight>
                  <thead>
                    <Tr>
                      <Th>{t('places.building')}</Th>
                      <Th>{t('places.list.placedBy')}</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {shownPlaces.map((place) => (
                      <Tr
                        key={place.id}
                        selected={editing?.placeId === place.id}
                        onClick={() => open(place)}
                      >
                        <Td>
                          <span className="flex flex-wrap items-center gap-1.5">
                            {place.line}
                            {place.names.length > 0 && (
                              <span className="text-secondary text-ink-3">{place.names.join(', ')}</span>
                            )}
                            {place.state === 'waiting' && (
                              <Pill tone="warn" soft>
                                {t('order.entrances.waiting')}
                              </Pill>
                            )}
                            {place.reported && (
                              <Pill tone="danger" soft>
                                {t('order.entrances.reported')}
                              </Pill>
                            )}
                          </span>
                          {place.note != null && <span className="block text-secondary text-ink-2">{place.note}</span>}
                        </Td>
                        <Td>
                          <span className="text-secondary text-ink-2">{who(t, place.source, place.placedBy, people)}</span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Section>
          </div>

          <div className="xl:sticky xl:top-0 min-w-0">
            <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
              <PlacesMap
                configuration={configuration}
                view={view}
                pins={pins}
                selected={editing?.placeId ?? shown?.place.id ?? null}
                onSelect={(id) => {
                  const place = list.places.find((candidate) => candidate.id === id);

                  if (place !== undefined) open(place);
                }}
                picking={editing === null ? null : picking}
                door={editing === null ? (shown === null ? null : (shown.suggested?.door ?? shown.place.door)) : editingDoor}
                stop={editing === null ? null : editingStop}
                doorLabel={editing?.draft.entrance ?? shown?.place.entrance ?? ''}
                onPick={pick}
                target={target}
                className="h-[calc(100vh-180px)] min-h-[420px]"
              />
            </Suspense>
            {editing !== null && (
              <p className="mt-2 mb-0 text-secondary text-ink-3">
                {picking === 'door' ? t('places.map.clickDoor') : t('places.map.clickStop')}
              </p>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={removing && editing?.placeId != null}
        title={t('places.confirmRemoveTitle')}
        confirm={t('places.editor.delete')}
        cancel={t('surcharges.cancelEdit')}
        onConfirm={() => {
          const id = editing?.placeId;

          setRemoving(false);
          if (id == null) return;

          void act(() => placesApi.remove(id), t('places.deleted')).then(() => {
            setEditing(null);
            setHistory(null);
          });
        }}
        onClose={() => setRemoving(false)}
      >
        {editing !== null &&
          t('places.confirmRemove', {
            line:
              editing.draft.entrance == null
                ? editing.draft.building
                : writeEntrance(editing.draft.building, editing.draft.entrance),
          })}
      </ConfirmDialog>

      <PlaceImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onImported={(added) => {
          setNotice(t('places.import.done', { count: added }));
          void reload();
        }}
      />
    </>
  );
}
